import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * ApprovalsBell — header alert for roadmap approval requests (R10).
 *
 * Polls every app's pending approval requests (GET /v1/app/:appId/roadmap/
 * approvals) and shows a bell with a count; the dropdown lists the items
 * and links to that app's Roadmap. Apps without the capability answer 403
 * and are skipped quietly. Rendered only when the `roadmap` capability is
 * not explicitly off for the instance.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { NexusAdminApiError, useNexusFetch } from '../api.js';
const POLL_MS = 120_000;
/** Router-free (tests, two React copies); the layout passes `navigate` for in-app links. */
export function ApprovalsBell({ basePath, navigate }) {
    const fetcher = useNexusFetch();
    const [items, setItems] = useState([]);
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const load = useCallback(async () => {
        try {
            const apps = (await fetcher('/v1/admin/apps'))?.apps ?? [];
            const all = await Promise.all(apps.filter((a) => a.status !== 'archived').map(async (a) => {
                try {
                    const r = await fetcher(`/v1/app/${a.appId}/roadmap/approvals`);
                    return (r.items ?? []).map((it) => ({ ...it, appId: a.appId, appName: a.displayName }));
                }
                catch (e) {
                    if (e instanceof NexusAdminApiError && (e.status === 403 || e.status === 404))
                        return [];
                    return [];
                }
            }));
            setItems(all.flat());
        }
        catch { /* keep what we had */ }
    }, [fetcher]);
    useEffect(() => {
        void load();
        const t = setInterval(() => void load(), POLL_MS);
        return () => clearInterval(t);
    }, [load]);
    useEffect(() => {
        if (!open)
            return;
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target))
            setOpen(false); };
        document.addEventListener('mousedown', onDoc);
        return () => document.removeEventListener('mousedown', onDoc);
    }, [open]);
    const n = items.length;
    return (_jsxs("div", { className: "position-relative me-3", ref: ref, "data-testid": "approvals-bell", children: [_jsxs("button", { type: "button", className: "btn btn-light border position-relative", "aria-label": n ? `${n} approval request${n === 1 ? '' : 's'} waiting` : 'No approval requests', title: n ? `${n} approval request${n === 1 ? '' : 's'} waiting` : 'No approval requests waiting', onClick: () => setOpen((v) => !v), children: [_jsx("span", { "aria-hidden": "true", children: "\uD83D\uDD14" }), n ? _jsx("span", { className: "position-absolute top-0 start-100 translate-middle badge rounded-pill bg-danger", "data-testid": "approvals-count", children: n }) : null] }), open ? (_jsxs("div", { className: "position-absolute end-0 mt-2 bg-white border rounded shadow-sm", style: { minWidth: 320, zIndex: 1050 }, children: [_jsx("div", { className: "px-3 py-2 border-bottom small text-muted", children: "Approval requests" }), n === 0 ? _jsx("div", { className: "px-3 py-2 small text-muted", children: "Nothing waiting for a decision." }) : (_jsx("ul", { className: "list-unstyled mb-0", style: { maxHeight: 360, overflowY: 'auto' }, children: items.map((it) => (_jsxs("li", { className: "px-3 py-2 border-bottom small", children: [_jsxs("a", { href: `${basePath}/roadmap?app=${encodeURIComponent(it.appId)}`, className: "text-decoration-none", onClick: (e) => { setOpen(false); if (navigate) {
                                        e.preventDefault();
                                        navigate(`${basePath}/roadmap?app=${encodeURIComponent(it.appId)}`);
                                    } }, children: [_jsx("span", { className: "font-monospace", children: it.key }), " ", _jsx("span", { className: "text-dark", children: it.title })] }), _jsxs("div", { className: "text-muted", children: [it.appName, it.estimateMinutes != null ? ` · ${Math.round((it.estimateMinutes / 60) * 10) / 10} h` : '', it.billable ? '' : ' · non-billable'] })] }, `${it.appId}:${it.id}`))) }))] })) : null] }));
}
//# sourceMappingURL=ApprovalsBell.js.map